import React, { useEffect, useState } from "react";
import { collection, query, getDocs } from "firebase/firestore";
import { db, auth } from "../components/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Cell, Legend, ResponsiveContainer, PieChart, Pie } from "recharts";

const COLORS = ["#8884d8", "#82ca9d", "#ffc658", "#ff7f50", "#a4de6c", "#d0ed57", "#a28bd4", "#ffb6b9", "#fae3d9", "#b5ead7", "#c7ceea", "#e2f0cb"];

const PantryCategoryChart = () => {
  const [data, setData] = useState([]);
  const [expiryData, setExpiryData] = useState([]);
  const [expiredLastMonthItems, setExpiredLastMonthItems] = useState([]);
  const [expiringThisMonthItems, setExpiringThisMonthItems] = useState([]);
  const [expiringNextMonthItems, setExpiringNextMonthItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);

  // Listen for user authentication
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserId(user.uid);
      } else {
        setUserId(null);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // Fetch data when userId changes
  useEffect(() => {
    if (!userId) return;

    const fetchCategoryData = async () => {
      try {
        const pantryRef = collection(db, "PantryList", userId, "pantry");
        const q = query(pantryRef);
        const snapshot = await getDocs(q);

        const categoryQuantity = {};
        let expiryGroup = {
          expiredLastMonth: 0,
          expiringThisMonth: 0,
          expiringNextMonth: 0,
        };

        const expiredLastMonthList = [];
        const expiringThisMonthList = [];
        const expiringNextMonthList = [];

        const now = new Date();
        const startOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const endOfThisMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
        const startOfNextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        const endOfNextMonth = new Date(now.getFullYear(), now.getMonth() + 2, 0);

        snapshot.forEach((doc) => {
          const item = doc.data();
          const category = item.category || "Other";
          const quantity = Number(item.quantity) || 1;

          // Category chart
          categoryQuantity[category] = (categoryQuantity[category] || 0) + quantity;

          // Expiry chart & group items
          if (item.expiryDate) {
            const expiryDate = item.expiryDate.toDate ? item.expiryDate.toDate() : new Date(item.expiryDate);

            const itemObj = {
              name: item.name || "Unnamed",
              category,
              quantity,
              expiryDate: expiryDate.toLocaleDateString(),
            };

            if (expiryDate >= startOfLastMonth && expiryDate <= endOfLastMonth) {
              expiryGroup.expiredLastMonth += quantity;
              expiredLastMonthList.push(itemObj);
            } else if (expiryDate >= startOfThisMonth && expiryDate <= endOfThisMonth) {
              expiryGroup.expiringThisMonth += quantity;
              expiringThisMonthList.push(itemObj);
            } else if (expiryDate >= startOfNextMonth && expiryDate <= endOfNextMonth) {
              expiryGroup.expiringNextMonth += quantity;
              expiringNextMonthList.push(itemObj);
            }
          }
        });

        const chartData = Object.entries(categoryQuantity).map(([category, quantity]) => ({
          category,
          quantity,
        }));

        const expiryChartData = [
          { name: "Expired Last Month", value: expiryGroup.expiredLastMonth },
          { name: "Expiring This Month", value: expiryGroup.expiringThisMonth },
          { name: "Expiring Next Month", value: expiryGroup.expiringNextMonth },
        ];

        setData(chartData);
        setExpiryData(expiryChartData);
        setExpiredLastMonthItems(expiredLastMonthList);
        setExpiringThisMonthItems(expiringThisMonthList);
        setExpiringNextMonthItems(expiringNextMonthList);
      } catch (error) {
        console.error("Error fetching pantry data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategoryData();
  }, [userId]);

  if (loading) {
    return <p className="text-center my-5">Loading pantry data...</p>;
  }

  if (!userId) {
    return <p className="text-center my-5">Please log in to view your pantry charts.</p>;
  }

  return (
    <div className="container my-5">
      <h2 className="text-center mb-4">Pantry Insights</h2>

      {/* Bar Chart */}
      <h5 className="text-center">Bar Chart (Quantity per Category)</h5>
      {data.length === 0 ? (
        <p className="text-center text-muted">No pantry items found.</p>
      ) : (
        <div style={{ width: "100%", height: 300 }}>
          <ResponsiveContainer>
            <BarChart data={data}>
              <XAxis dataKey="category" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar dataKey="quantity" fill="#8884d8">
                {data.map((entry, index) => (
                  <Cell key={`bar-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Expiry Chart */}
      <h5 className="text-center mt-5">Expiry Insights (Last, Current, Next Month)</h5>
      {expiryData.every((item) => item.value === 0) ? (
        <p className="text-center text-muted">No expiry data available.</p>
      ) : (
        <div style={{ width: "100%", height: 300, marginTop: "1rem" }}>
          <ResponsiveContainer>
            <PieChart>
              <Pie data={expiryData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} innerRadius={50} label minAngle={5}>
                {expiryData.map((entry, index) => (
                  <Cell key={`expiry-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Month-wise tables */}
      <h5 className="mt-4">Expired Last Month</h5>
      {expiredLastMonthItems.length === 0 ? (
        <p className="text-muted">No items expired last month.</p>
      ) : (
        <table className="table table-striped mt-2">
          <thead>
            <tr>
              <th>Item Name</th>
              <th>Category</th>
              <th>Quantity</th>
              <th>Expiry Date</th>
            </tr>
          </thead>
          <tbody>
            {expiredLastMonthItems.map((item, index) => (
              <tr key={index}>
                <td>{item.name}</td>
                <td>{item.category}</td>
                <td>{item.quantity}</td>
                <td>{item.expiryDate}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h5 className="mt-4">Expiring This Month</h5>
      {expiringThisMonthItems.length === 0 ? (
        <p className="text-muted">No items expiring this month.</p>
      ) : (
        <table className="table table-striped mt-2">
          <thead>
            <tr>
              <th>Item Name</th>
              <th>Category</th>
              <th>Quantity</th>
              <th>Expiry Date</th>
            </tr>
          </thead>
          <tbody>
            {expiringThisMonthItems.map((item, index) => (
              <tr key={index}>
                <td>{item.name}</td>
                <td>{item.category}</td>
                <td>{item.quantity}</td>
                <td>{item.expiryDate}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h5 className="mt-4">Expiring Next Month</h5>
      {expiringNextMonthItems.length === 0 ? (
        <p className="text-muted">No items expiring next month.</p>
      ) : (
        <table className="table table-striped mt-2">
          <thead>
            <tr>
              <th>Item Name</th>
              <th>Category</th>
              <th>Quantity</th>
              <th>Expiry Date</th>
            </tr>
          </thead>
          <tbody>
            {expiringNextMonthItems.map((item, index) => (
              <tr key={index}>
                <td>{item.name}</td>
                <td>{item.category}</td>
                <td>{item.quantity}</td>
                <td>{item.expiryDate}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default PantryCategoryChart;
